import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemIcon,
  Stack,
  Dialog,
  Paper,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Schedule as ScheduleIcon,
  Devices as DevicesIcon,
  DateRange as DateRangeIcon,
  Timer as TimerIcon,
  Monitor as MonitorIcon,
  SmartDisplay as SmartDisplayIcon,
  Scale as ScaleIcon,
  InfoOutlined as InfoIcon,
  LabelOutlined as LabelIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Restaurant as RestaurantIcon,
  ConfirmationNumber as TicketIcon,
  LowPriority as PriorityIcon,
  People as QueueIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ArticleIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import ReactPlayer from "react-player";
import { it } from "date-fns/locale";

// --- HELPERS ---

const formatPlayerTime = (secondsStr) => {
  if (!secondsStr) return null;
  const seconds = parseInt(secondsStr, 10);
  if (isNaN(seconds) || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const getFileType = (fileName, item) => {
  if (!fileName) return "unsupported";
  const extension = fileName.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) return "image";
  if (["mp4", "mov", "avi", "wmv", "webm"].includes(extension)) return "video";
  if (extension === "pdf") return "pdf";
  if (item.content_type === "TXT") return "text";
  return "unsupported";
};

const DeviceTypeIcon = ({ type }) => {
  const theme = useTheme();
  const color = theme.palette.action.active; 
  switch (String(type)) {
    case "1": return <MonitorIcon fontSize="small" sx={{color}} />;
    case "2": return <SmartDisplayIcon fontSize="small" sx={{color}} />;
    case "3": return <ScaleIcon fontSize="small" sx={{color}} />;
    default: return <DevicesIcon fontSize="small" sx={{color}} />;
  }
};

// FIX: Spostato useTheme prima del return condizionale
const PlayDays = ({ days }) => {
  const theme = useTheme(); // Hook chiamato SEMPRE all'inizio
  
  if (!days) return null;
  
  const dayChars = ["L", "M", "M", "G", "V", "S", "D"];
  
  return (
    <Stack direction="row" spacing={0.5}>
      {dayChars.map((day, index) => {
        const isActive = days[index] === "1";
        return (
        <Box
          key={index}
          sx={{
            width: 18, height: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: isActive ? theme.palette.primary.main : 'transparent',
            color: isActive ? theme.palette.primary.contrastText : theme.palette.text.disabled,
            border: isActive ? 'none' : `1px solid ${theme.palette.divider}`,
            fontSize: '0.6rem', fontWeight: 700
          }}
        >
          {day}
        </Box>
      )})}
    </Stack>
  );
};

const mediaScheduling = (item) => {
  if (!item.play_date_start) return "Unknown";
  const currentDateTime = new Date();
  const currentDayOfWeek = currentDateTime.getDay();
  const playDaysArray = (item.play_days || "1111111").split("").map((d) => d === "1");
  const isTodayPlayable = playDaysArray[(currentDayOfWeek + 6) % 7];
  const startDate = new Date(item.play_date_start.replace(/\//g, "-"));
  const endDate = new Date(item.play_date_end.replace(/\//g, "-"));
  endDate.setHours(23, 59, 59, 999);
  const startTime = new Date(`1970-01-01T${item.play_time_start || "00:00:00"}`);
  const endTime = new Date(`1970-01-01T${item.play_time_end || "23:59:59"}`);
  const currentTime = new Date(`1970-01-01T${currentDateTime.toTimeString().slice(0, 8)}`);

  if (currentDateTime < startDate) return "Scheduled";
  else if (currentDateTime > endDate) return "Expired";
  else {
    if (isTodayPlayable && currentTime >= startTime && currentTime <= endTime) return "Playing";
    else if (isTodayPlayable && currentTime < startTime) return "Scheduled";
    else return "Paused";
  }
};

const getSchedulingStatusProps = (status) => {
  switch (status) {
    case "Playing": return { color: "success", icon: <SmartDisplayIcon />, label: "In Onda" };
    case "Scheduled": return { color: "info", icon: <ScheduleIcon />, label: "Programmato" };
    case "Paused": return { color: "warning", icon: <PauseIcon />, label: "Pausa" };
    case "Expired": return { color: "error", icon: <HistoryIcon />, label: "Scaduto" };
    default: return { color: "default", icon: <InfoIcon />, label: status };
  }
};

// --- COMPONENTE PRINCIPALE ---

export default function ContentCardV2({ item, onDelete }) {
  const theme = useTheme();
  const [devicesAnchorEl, setDevicesAnchorEl] = useState(null);
  const [totemTargetsAnchorEl, setTotemTargetsAnchorEl] = useState(null);
  const [isImageDialogOpen, setImageDialogOpen] = useState(false);
  const [textContent, setTextContent] = useState(null);

  // Spostiamo i calcoli derivati qui, ma non i return condizionali degli hook
  const fileType = getFileType(item?.original_name, item);
  const fullUrlThumbs = item ? item.web_thumbnail_path : null;
  const fullUrlFullSize = item ? item.web_path : null;
  const isTotemItem = item?.media_target === "EXTRA";
  const totemData = item?.json_data || item;

  // useEffect è un hook, deve essere chiamato incondizionatamente (prima di "if (!item)")
  useEffect(() => {
    if (isImageDialogOpen && fileType === "text" && fullUrlFullSize) {
      setTextContent(item.content_value);
    }
  }, [isImageDialogOpen, fileType, fullUrlFullSize, item]);

  // Ora possiamo fare l'early return
  if (!item) return null;

  const {
    image_state,
    original_name,
    image_desc,
    content_set,
    play_date_start,
    play_date_end,
    play_days,
    device_ids = [],
    player_time,
  } = item;

  const isScheduled = image_state === "WEB";
  const statusColor = isScheduled ? "success" : "warning";
  const schedulingStatus = mediaScheduling(item);
  const { color: schedulingColor, icon: schedulingIcon, label: schedulingLabel } = getSchedulingStatusProps(schedulingStatus);

  const handleOpenImageDialog = () => setImageDialogOpen(true);
  const handleCloseImageDialog = () => setImageDialogOpen(false);

  // --- STYLED COMPONENTS INTERNI ---
  const DataListItem = ({ icon, label, value, onClick, button, sx }) => (
    <ListItem
      disableGutters
      button={button}
      onClick={onClick}
      sx={{
        py: 0.75, px: 1,
        borderRadius: 2,
        '&:hover': button ? { backgroundColor: alpha(theme.palette.primary.main, 0.08) } : {},
        ...sx
      }}
    >
      <ListItemIcon sx={{ minWidth: 34, color: theme.palette.action.active }}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={<Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>{label}</Typography>}
        secondary={<Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>{value}</Typography>}
      />
    </ListItem>
  );

  // --- RENDERING MEDIA PREVIEW ---
  const MediaViewer = () => {
    const placeholderSx = {
        height: 200, width: "100%",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.divider}`
    };

    switch (fileType) {
      case "video":
        return (
          <Box sx={{ position: "relative", lineHeight: 0, bgcolor: 'black', height: 200 }}>
            <Box
              component={ReactPlayer}
              src={fullUrlFullSize}
              muted={true} playing={false} width="100%" height="100%" style={{ objectFit: "contain" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.3)" }}>
              <SmartDisplayIcon sx={{ fontSize: 48, color: "white", opacity: 0.9, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            </Box>
          </Box>
        );
      case "text":
        return (
          <Box sx={placeholderSx}>
            <DescriptionIcon sx={{ fontSize: 56, mb: 1, color: theme.palette.primary.main, opacity: 0.8 }} />
            <Typography variant="caption" fontWeight={700}>DOCUMENTO DI TESTO</Typography>
          </Box>
        );
      case "pdf":
        return (
            <Box sx={placeholderSx}>
            <DescriptionIcon sx={{ fontSize: 56, mb: 1, color: theme.palette.error.main, opacity: 0.8 }} />
            <Typography variant="caption" fontWeight={700}>DOCUMENTO PDF</Typography>
          </Box>
        );
      case "image":
      default:
        return (
          <Box sx={{ height: 200, bgcolor: 'grey.100' }}>
            <Box component="img" src={fullUrlThumbs} alt={original_name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
        );
    }
  };

  return (
    <>
      <Card
        sx={{
          display: "flex", flexDirection: "column", height: "100%", position: "relative",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[0],
          borderRadius: 2,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 12px 24px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
            borderColor: alpha(theme.palette.primary.main, 0.5),
          },
        }}
      >
        <Box sx={{ position: "relative", cursor: "pointer" }} onClick={handleOpenImageDialog}>
          <MediaViewer />

          <Tooltip title="Ingrandisci">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleOpenImageDialog(); }}
              sx={{
                position: "absolute", top: 10, left: 10, color: "white",
                bgcolor: "rgba(0, 0, 0, 0.3)",
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.1)',
                "&:hover": { bgcolor: theme.palette.primary.main },
              }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Stack direction="row" spacing={0.8} sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}>
            <Chip
              label={image_state}
              color={statusColor}
              size="small"
              sx={{ fontWeight: 700, height: 22, fontSize: '0.65rem', backdropFilter: 'blur(4px)' }}
            />
            {isScheduled && (
              <Chip
                icon={schedulingIcon}
                label={schedulingLabel}
                size="small"
                sx={{
                  fontWeight: 700, height: 22, fontSize: '0.65rem',
                  bgcolor: alpha(theme.palette[schedulingColor].main, 0.9),
                  color: theme.palette[schedulingColor].contrastText,
                  backdropFilter: 'blur(4px)',
                  '& .MuiChip-icon': { color: 'inherit' }
                }}
              />
            )}
          </Stack>

          {fileType === "video" && formatPlayerTime(player_time) && (
            <Chip
              icon={<TimerIcon style={{ fontSize: '0.9rem' }} />}
              label={formatPlayerTime(player_time)}
              size="small"
              sx={{
                position: "absolute", bottom: 8, right: 8, zIndex: 1,
                bgcolor: "rgba(0,0,0,0.6)", color: "white",
                backdropFilter: 'blur(4px)', height: 22, fontSize: '0.7rem',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2, pb: 1.5, display: 'flex', flexDirection: 'column' }}>
          <Tooltip title={image_desc || original_name}>
            <Typography variant="subtitle1" noWrap sx={{ mb: 0.5, fontWeight: 700 }}>
              {isTotemItem && item.content_type === "TXT" ? item.content_value.slice(0, 20) + "..." : original_name}
            </Typography>
          </Tooltip>
           <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mb: 2 }}>
             {image_desc || "Nessuna descrizione"}
          </Typography>

          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DataListItem
                icon={<DateRangeIcon fontSize="small" />}
                label="Periodo"
                value={play_date_start === play_date_end
                    ? format(new Date(play_date_start.replace(/\//g, "-")), "d MMM yy", { locale: it })
                    : `${format(new Date(play_date_start.replace(/\//g, "-")), "d MMM")} - ${format(new Date(play_date_end.replace(/\//g, "-")), "d MMM")}`
                }
            />
            <DataListItem
                icon={<ScheduleIcon fontSize="small" />}
                label="Giorni Attivi"
                value={play_days === "1111111" ? "Tutti i giorni" : <PlayDays days={play_days} />}
            />

            {isTotemItem && (
              <Paper variant="outlined" sx={{ 
                  mt: 1.5, mb: 1.5, p: 1, borderRadius: 3,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  borderColor: alpha(theme.palette.secondary.main, 0.2),
                  display: 'flex', flexDirection: 'column', gap: 0.5
              }}>
                 <Typography variant="caption" color="secondary" sx={{ px:1, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Configurazione Totem
                 </Typography>
                 <DataListItem
                    icon={totemData.print_function === 1 ? <TicketIcon fontSize="small" color="secondary" /> : <RestaurantIcon fontSize="small" color="secondary" />}
                    label="Funzione Stampa"
                    value={totemData.print_function === 1 ? "Prenotazione Ticket" : "Stampa Ingredienti"}
                    sx={{ '& .MuiListItemText-secondary': { color: theme.palette.secondary.main } }} 
                  />
                  <DataListItem
                    icon={<PriorityIcon fontSize="small" color="secondary" />}
                    label="Logica Coda"
                    value={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`Priorità: ${totemData.content_priority || 5}`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.dark, fontWeight: 600 }} />
                          <Chip size="small" label={`Soglia > ${totemData.print_cond_queue_min || 0}`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.dark, fontWeight: 600 }} />
                        </Stack>
                    }
                  />
                   <DataListItem
                    button
                    onClick={(e) => setTotemTargetsAnchorEl(e.currentTarget)}
                    icon={<StorefrontIcon fontSize="small" color="secondary" />}
                    label="Target Attivi"
                    value={<Typography variant="body2" sx={{ textDecoration: 'underline', cursor: 'pointer', color: theme.palette.secondary.main, fontWeight: 600 }}>Vedi Banchi/Articoli</Typography>}
                  />
              </Paper>
            )}
          </List>
          
          <Box sx={{ flexGrow: 1 }} />
          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box
                onClick={(e) => setDevicesAnchorEl(e.currentTarget)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    p: 0.5, pr: 1.5, borderRadius: 10,
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.05) }
                }}
            >
                <DevicesIcon fontSize="small" color="action" sx={{ opacity: 0.7 }} />
                 <Typography variant="caption" fontWeight={600} color="text.primary">
                    {device_ids.length} Dispositivi
                </Typography>
            </Box>

             <Tooltip title={`Content Set: ${content_set}`}>
                <Chip
                    icon={<LabelIcon fontSize="small" />}
                    label={content_set || "Default"}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.65rem', fontWeight: 600, borderColor: theme.palette.divider, '& .MuiChip-icon': { fontSize: '0.9rem', color: theme.palette.text.disabled } }}
                />
            </Tooltip>
          </Stack>
        </CardContent>

        <Popover
          open={Boolean(devicesAnchorEl)}
          anchorEl={devicesAnchorEl}
          onClose={() => setDevicesAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{ elevation: 8, sx: { borderRadius: 3, mt: 1 } }}
        >
          <List dense sx={{ minWidth: 250, p: 1 }}>
            <ListSubheader sx={{ fontWeight: 700, lineHeight: '32px' }}>Dispositivi Assegnati</ListSubheader>
            {device_ids.length > 0 ? (
              device_ids.map((dev, index) => (
                <ListItem key={index} sx={{ borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <DeviceTypeIcon type={dev.single_dev_type} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{dev.single_dev_desc || dev.single_dev_id}</Typography>}
                    secondary={dev.single_dev_id === "ALL" ? "Tutti i dispositivi" : `ID: ${dev.single_dev_id}`}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem><ListItemText secondary="Nessun dispositivo specifico." /></ListItem>
            )}
          </List>
        </Popover>

        <Popover
          open={Boolean(totemTargetsAnchorEl)}
          anchorEl={totemTargetsAnchorEl}
          onClose={() => setTotemTargetsAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{ elevation: 8, sx: { borderRadius: 3, mt: 1, borderColor: theme.palette.secondary.light } }}
        >
          <List dense sx={{ minWidth: 250, p:1 }}>
            <ListSubheader sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, color: theme.palette.secondary.dark }}>
              <StorefrontIcon fontSize="small" /> Banchi Abilitati
            </ListSubheader>
            {totemData.print_cond_banco && totemData.print_cond_banco.length > 0 ? (
              (Array.isArray(totemData.print_cond_banco) ? totemData.print_cond_banco : totemData.print_cond_banco.split(",")).map((c, i) => (
                <ListItem key={"c" + i} sx={{ py:0 }}><ListItemText primary={<Typography variant="body2">{c}</Typography>} /></ListItem>
              ))
            ) : (
              <ListItem sx={{ py:0 }}><ListItemText secondary="Nessun banco trigger" /></ListItem>
            )}

            <Divider sx={{ my: 1, opacity: 0.5 }} />

            <ListSubheader sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, color: theme.palette.secondary.dark }}>
              <ArticleIcon fontSize="small" /> Articoli Trigger
            </ListSubheader>
            {totemData.print_cond_arts && totemData.print_cond_arts.length > 0 ? (
              (Array.isArray(totemData.print_cond_arts) ? totemData.print_cond_arts : totemData.print_cond_arts.split(",")).map((a, i) => (
                <ListItem key={"a" + i} sx={{ py:0 }}><ListItemText primary={<Typography variant="body2">{a}</Typography>} /></ListItem>
              ))
            ) : (
              <ListItem sx={{ py:0 }}><ListItemText secondary="Nessun articolo trigger" /></ListItem>
            )}
          </List>
        </Popover>
      </Card>

      <Dialog
        open={isImageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="xl"
        BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' } }}
        PaperProps={{
          sx: {
            backgroundColor: fileType === "text" ? "background.paper" : "transparent",
            boxShadow: fileType === "text" ? 24 : "none",
            overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: "50vw", minHeight: "50vh", borderRadius: 3
          },
        }}
      >
        <Tooltip title="Chiudi">
          <IconButton
            onClick={handleCloseImageDialog}
            sx={{
              position: "absolute", right: 16, top: 16,
              color: fileType === "text" ? "text.primary" : "white",
              backgroundColor: fileType === "text" ? alpha(theme.palette.grey[200], 0.5) : "rgba(0, 0, 0, 0.5)",
              backdropFilter: 'blur(4px)',
              "&:hover": {
                backgroundColor: fileType === "text" ? alpha(theme.palette.grey[300], 0.8) : "rgba(0, 0, 0, 0.8)",
              },
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>

        {(() => {
          switch (fileType) {
            case "video":
              return (
                <ReactPlayer url={fullUrlFullSize} controls playing width="100%" height="auto" style={{ maxWidth: "90vw", maxHeight: "90vh" }} />
              );
            case "text":
              return (
                <Paper elevation={0} sx={{ p: 4, width: "100%", maxWidth: 800, maxHeight: "80vh", overflowY: "auto" }}>
                  <Typography variant="h5" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 2, fontWeight: 700 }}>
                    <DescriptionIcon color="primary" /> Contenuto Ticket
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: alpha(theme.palette.action.hover, 0.3), borderColor: theme.palette.divider, borderRadius: 2 }}>
                    <Typography variant="body1" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "'Courier New', monospace", m: 0 }}>
                      {textContent || "Nessun testo disponibile."}
                    </Typography>
                  </Paper>
                </Paper>
              );
            case "image":
              return (
                <img src={fullUrlFullSize} alt="Preview" style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} />
              );
            default:
              return <Typography sx={{ color: "white" }}>Anteprima non disponibile</Typography>;
          }
        })()}
      </Dialog>
    </>
  );
}